<template>
  <div style="min-height: calc(100vh - 176px)" class="flex flex-wrap mt-4">
    <div 
      class="relative py-2 px-2 flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-blueGray-100 border-0" 
      style="min-height: calc(-176px + 100vh);"
    >
      <div class="bg-white shadow-md p-4 rounded-lg flex flex-wrap gap-4 mb-4">
        <input
          type="text"
          v-model="searchQuery"
          @input="updateFilters"
          placeholder="Поиск по заголовку..."
          class="border p-2 rounded w-full sm:w-1/3"
        />
        <select
          v-model="sortField"
          @change="updateFilters"
          class="border px-3 py-2 pr-7 rounded whitespace-nowrap"
        >
          <option value="created_at">По дате создания</option>
          <option value="title">По заголовку</option>
        </select>
        <button
          @click="toggleSortOrder"
          class="border px-3 py-2 rounded bg-gray-100 hover:bg-gray-200 whitespace-nowrap"
        >
          {{ sortOrder === 'asc' ? '↑ Возрастание' : '↓ Убывание' }}
        </button>
        <select v-model="selectedCategory" @change="updateFilters" class="border p-2 rounded w-full sm:w-1/4">
          <option value="">Все категории</option>
          <option v-for="category in categories" :key="category.id" :value="category.id">
            {{ category.name }}
          </option>
        </select>
        <select v-model="selectedStatus" @change="updateFilters" class="border p-2 rounded w-full sm:w-1/4">
          <option value="">Все статусы</option>
          <option value="true">Одобрено</option>
          <option value="false">Не одобрено</option>
        </select>
        <router-link 
          :to="{ path: '/admin/content/create', query: { type: contentType } }" 
          class="bg-blue-500 text-white px-4 py-2 rounded"
        >
          Создать
        </router-link>
      </div>

      <div class="bg-white shadow-md rounded-lg overflow-hidden">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="bg-gray-200">
              <th class="p-3">#</th>
              <th 
                
                class="p-3">Изображение</th>
              <th class="p-3">Заголовок</th>
              <th class="p-3">Категория</th>
              <th class="p-3">Автор</th>
              <th class="p-3">Дата</th>
              <th class="p-3">Статус</th>
              <th class="p-3">Действия</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(news, index) in newsList" :key="index" class="border-t">
              <td class="p-3">{{ index + 1 + (currentPage - 1) * perPage }}</td>
              <td class="p-3">
                <img v-if="news.image" :src="news.image" class="w-16 h-16 object-cover rounded" />
                <span v-else class="text-gray-500">Нет изображения</span>
              </td>
              <td class="p-3">
                <router-link :to="`/admin/content/edit/${news.id}`" class="text-blue-600 hover:underline">
                  {{ news.title }}
                </router-link>
              </td>
              <td class="p-3">{{ news.category_name || 'Без категории' }}</td>
              <td class="p-3">{{ news.author_fio }}</td>
              <td class="p-3">{{ news.created_at }}</td>
              <td class="p-3">
                <span :class="news.is_approved ? 'text-green-600' : 'text-red-600'">
                  {{ news.is_approved ? 'Одобрено' : 'Не одобрено' }}
                </span>
              </td>
              <td class="p-3 flex gap-2 items-center">
                <div style="margin-top: 5%;" class="h-full">
                  <button v-if="!news.is_approved" @click="approveNews(news.id)" class="bg-green-500 text-white px-3 py-1 rounded">
                    Одобрить
                  </button>
                  <button v-if="news.is_approved" @click="rejectNews(news.id)" class="bg-red-500 text-white px-3 py-1 rounded">
                    Отклонить
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex justify-center mt-4">
        <button @click="changePage(currentPage - 1)" :disabled="currentPage === 1" class="px-4 py-2 bg-gray-300 rounded mr-2">
          ← Назад
        </button>
        <span class="px-4 py-2">Страница {{ currentPage }} из {{ totalPages }}</span>
        <button @click="changePage(currentPage + 1)" :disabled="currentPage >= totalPages" class="px-4 py-2 bg-gray-300 rounded ml-2">
          Вперед →
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { API_NEWS, HEADERS_TOKEN_JSON } from "@/const/uri";

export default {
  data() {
    return {
      newsList: [],
      categories: [],
      searchQuery: "",
      selectedCategory: "",
      selectedStatus: "",
      currentPage: 1,
      perPage: 10,
      totalPages: 1,
      contentType: "",
      sortField: "created_at",
      sortOrder: "desc",
    };
  },
  watch: {
    "$route.query": {
      immediate: true,
      handler() {
        this.fetchNews();
      },
    },
    "$route.params.type": {
      immediate: true,
      handler(newType) {
        this.contentType = newType || "";
        this.fetchNews();
        this.fetchCategories();
      },
    },
  },
  mounted() {
    this.contentType = this.$route.params.type || "";
    this.fetchCategories();
  },
  methods: {
    async fetchNews() {
      const query = this.$route.query;

      this.searchQuery = query.search || "";
      this.selectedCategory = query.category || "";
      this.selectedStatus = query.is_approved || "";
      this.currentPage = Number(query.page) || 1;
      this.sortField = query.sort || "created_at";
      this.sortOrder = query.order || "desc";
      this.contentType = this.$route.params.type || "";

      if (!this.contentType) {
        console.error("Content type is required");
        return;
      }

      const orderingField = this.sortOrder === 'desc' ? `-${this.sortField}` : this.sortField;

      await axios({
        method: "GET",
        url: API_NEWS.list,
        params: {
          search: this.searchQuery,
          category: this.selectedCategory,
          is_approved: this.selectedStatus,
          p: this.currentPage,
          page_size: this.perPage,
          content_type: this.contentType,
          ordering: orderingField,
        },
        headers: HEADERS_TOKEN_JSON(),
      })
        .then((e) => {
          if (e.data.code === 0) {
            const responseData = e.data.data;
            this.newsList = (responseData.results || []).filter(item => item !== null);
            this.totalPages = Math.ceil(responseData.count / this.perPage);
          }
        })
        .catch((e) => {
          console.log("Ошибка загрузки новостей:", e);
        });
    },
    async fetchCategories() {
      await axios({
        method: "GET",
        url: API_NEWS.category,
        params: {
          content_type: this.contentType,
        },
        headers: HEADERS_TOKEN_JSON(),
      })
        .then((e) => {
          if (e.data.code === 0) {
            this.categories = e.data.data.results || e.data.data;
          }
        })
        .catch((e) => {
          console.log("Ошибка загрузки категорий:", e);
        });
    },
    async approveNews(id) {
      try {
        await axios.post(`${API_NEWS.list}${id}/approve/`, {}, {
          headers: HEADERS_TOKEN_JSON(),
        });
        this.fetchNews();
      } catch (error) {
        console.error("Ошибка одобрения новости:", error);
      }
    },
    async rejectNews(id) {
      try {
        await axios.post(`${API_NEWS.list}${id}/reject/`, {}, {
          headers: HEADERS_TOKEN_JSON(),
        });
        this.fetchNews();
      } catch (error) {
        console.error("Ошибка отклонения новости:", error);
      }
    },
    toggleSortOrder() {
      this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      this.updateFilters();
    },
    updateFilters() {
      this.$router.push({
        query: {
          search: this.searchQuery || undefined,
          category: this.selectedCategory || undefined,
          is_approved: this.selectedStatus || undefined,
          sort: this.sortField,
          order: this.sortOrder,
          page: 1,
        },
        params: {
          type: this.contentType,
        },
      });
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.$router.push({ query: { ...this.$route.query, page } });
      }
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString("ru-RU", { year: "numeric", month: "long", day: "numeric" });
    },
  },
};
</script>
